import React, { useContext, useEffect, useState } from 'react'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import { useNavigate } from 'react-router-dom'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { TablePagination, Typography } from '@mui/material'

// API services
import { useGetAllFilteredLeaveApproval } from 'services/api/modules/leave/leaveApproval'

import { authContext } from 'contexts/AuthContext'
import UpskillPendingTab from 'pages/approval-center/components/upskill/UpskillPendingTab'
import UpskillApprovedTab from 'pages/approval-center/components/upskill/UpskillApprovedTab'
import { getUpskillApprovalList } from 'services/api/modules/upskill/approval-center'
import { useQuery } from 'react-query'
import UpskillApprovalTab from 'pages/approval-center/components/upskill/UpskillApprovalTab'

const UpskillApprovalCenterOverview = ({ initialTab, module }) => {
  const {
    auth: {
      data: { permissions },
    },
  } = useContext(authContext)

  // change state from child to parent
  const [tabIndex, setTabIndex] = useState(initialTab)
  const { data: pendingData } = useGetAllFilteredLeaveApproval({
    status: 'pending',
  })

  // useEffect
  useEffect(() => {
    changeRoute(tabIndex)
  }, [tabIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // function to change route based on tabIndex
  const navigate = useNavigate()
  const changeRoute = (index) => {
    switch (index) {
      case 0:
        navigate(`/app/approval-center/upskill/pending`)
        break
      case 1:
        navigate(`/app/approval-center/upskill/approved`)
        break
      case 2:
        navigate(`/app/approval-center/upskill/rejected`)
        break

      default:
        break
    }
  }
  // page for pending tab
  const [pendingPage, setPendingPage] = useState(0)
  const [rowsPerPendingPage, setRowsPerPendingPage] = useState(5)
  const handleChangePendingPage = (event, newPage) => {
    setPendingPage(newPage)
  }
  const handleChangeRowsPerPendingPage = (event) => {
    setRowsPerPendingPage(parseInt(event.target.value, 10))
    setPendingPage(0)
  }
  // page for aproved tab
  const [approvedPage, setApprovedPage] = useState(0)
  const [rowsPerApprovedPage, setRowsPerApprovedPage] = useState(5)
  const handleChangeApprovedPage = (event, newPage) => {
    setApprovedPage(newPage)
  }
  const handleChangeRowsPerApprovedPage = (event) => {
    setRowsPerApprovedPage(parseInt(event.target.value, 10))
    setApprovedPage(0)
  }
  // page for rejected tab
  const [rejectedPage, setRejectedPage] = useState(0)
  const [rowsPerRejectedPage, setRowsPerRejectedPage] = useState(5)
  const handleChangeRejectedPage = (event, newPage) => {
    setRejectedPage(newPage)
  }
  const handleChangeRowsPerRejectedPage = (event) => {
    setRowsPerRejectedPage(parseInt(event.target.value, 10))
    setRejectedPage(0)
  }
  const {
    isLoading: pendingIsLoading,
    error: pendingIsError,
    data: pendingDataList,
  } = useQuery(
    ['pendingUpskillList', pendingPage, rowsPerPendingPage],
    async () => {
      const { data } = await getUpskillApprovalList('pending', rowsPerPendingPage, pendingPage + 1)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const {
    isLoading: approvedIsLoading,
    error: approvedIsError,
    data: approvedDataList,
  } = useQuery(
    ['approvedDataList', approvedPage, rowsPerApprovedPage],
    async () => {
      const { data } = await getUpskillApprovalList(
        'approved',
        rowsPerApprovedPage,
        approvedPage + 1
      )
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const {
    isLoading: rejectedIsLoading,
    error: rejectedIsError,
    data: rejectedDataList,
  } = useQuery(
    ['rejectedDataList', rejectedPage, rowsPerRejectedPage],
    async () => {
      const { data } = await getUpskillApprovalList(
        'rejected',
        rowsPerRejectedPage,
        rejectedPage + 1
      )
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  return (
    <DashboardContainer
      headerTitle={
        <Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
          {module}
        </Typography>
      }
    >
      <Typography
        display={{ xs: 'block', lg: 'none' }}
        variant="h4"
        color="light-gray-text.main"
        mt={3}
        mb={2}
      >
        Upskill requests
      </Typography>
      <AdxTabsBadge
        scrollButtons={false}
        sxPanel={{
          bgcolor: 'transparent',
          borderRadius: 0,
          overflowY: 'unset',
          boxSizing: 'border-box',
        }}
        setTabIndex={setTabIndex}
        initialValue={initialTab}
      >
        <AdxTabsPanel
          label="Pending"
          notification={pendingDataList?.meta?.total == '0' ? '0' : pendingDataList?.meta?.total}
        >
          <UpskillApprovalTab
            data={pendingDataList}
            isLoading={pendingIsLoading}
            isError={pendingIsError}
            type="pending"
            lastPage={pendingDataList?.meta?.last_page}
            count={pendingDataList?.meta?.total}
            handleChangePage={handleChangePendingPage}
            page={pendingPage}
            rowsPerPage={rowsPerPendingPage}
            handleChangeRows={handleChangeRowsPerPendingPage}
          />
        </AdxTabsPanel>
        <AdxTabsPanel
          label="Approved"
          notification={approvedDataList?.meta?.total == '0' ? '0' : approvedDataList?.meta?.total}
        >
          <UpskillApprovalTab
            data={approvedDataList}
            isLoading={approvedIsLoading}
            isError={approvedIsError}
            type="approved"
            lastPage={approvedDataList?.meta?.last_page}
            count={approvedDataList?.meta?.total}
            handleChangePage={handleChangeApprovedPage}
            page={approvedPage}
            rowsPerPage={rowsPerApprovedPage}
            handleChangeRows={handleChangeRowsPerApprovedPage}
          />
        </AdxTabsPanel>
        <AdxTabsPanel
          label="Rejected"
          notification={rejectedDataList?.meta?.total == '0' ? '0' : rejectedDataList?.meta?.total}
        >
          <UpskillApprovalTab
            data={rejectedDataList}
            isLoading={rejectedIsLoading}
            isError={rejectedIsError}
            type="rejected"
            lastPage={rejectedDataList?.meta?.last_page}
            count={rejectedDataList?.meta?.total}
            handleChangePage={handleChangeRejectedPage}
            page={rejectedPage}
            rowsPerPage={rowsPerRejectedPage}
            handleChangeRows={handleChangeRowsPerRejectedPage}
          />
        </AdxTabsPanel>
      </AdxTabsBadge>
    </DashboardContainer>
  )
}

export default UpskillApprovalCenterOverview
